import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { Moment } from "moment";
import * as dataCore from "../../core/datacore";
import { Subject, Subscription } from "rxjs";
import { HttpResponse } from "@angular/common/http";
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { WorkplaceService } from "../../services/workplace.service";
import { SnackBarService } from "../../shared/snackbar.service";
import { ActivityTypeService } from "../../services/activity-type.service";
import { JobTypeService } from "../../services/job-type.service";
import { PersonService } from "../../services/person.service";
import { WorkgroupService } from "../../services/workgroup.service";
import { ActivityService, WorkplaceActivityDto } from "../../services/activity.service";
import moment = require("moment");
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialog } from "../../shared/confirm-dialog.component";

@Component({
    selector: "crew-day-activities",
    template: require('./day-activities.component.html')
})
export class DayActitiesComponent implements OnInit, OnDestroy {
    isLoading = false;

    activityRegistrationInfo: ActivityRegistrationInfoDto = <any>{};
    registeredActivitiesFilter: RegisteredActivitiesFilter = <any>{};

    persons: Nema.Crew.Model.Person[];
    workplaceActivities: WorkplaceActivityDto[];

    workplaces: Nema.Crew.Model.Workplace[];
    workgroups: Nema.Crew.Model.WorkGroup[];
    activityTypes: Nema.Crew.Model.ActivityType[];
    jobTypes: Nema.Crew.Model.JobType[];

    selectedDayChanged = new Subject<Moment>();
    selectedDayChangedSubscription: Subscription;

    isFetchingPersons = false;
    isFetchingActivities = false;

    constructor(
        private personService: PersonService,
        private activityService: ActivityService,
        private workplaceService: WorkplaceService,
        private workgroupService: WorkgroupService,
        private activityTypeService: ActivityTypeService,
        private jobTypeService: JobTypeService,
        private dialog: MatDialog,
        private snackBarService: SnackBarService,
        @Inject("date") private selectedDate: Date,
        @Inject("workplaceId") private selectedWorkplaceId: number,
        @Inject("workgroupId") private selectedWorkgroupId: number
    ) {
        this.selectedDayChangedSubscription = this.selectedDayChanged
            .pipe(debounceTime(200), distinctUntilChanged())
            .subscribe((day: Moment) => this.loadDataForDay(day));
    }

    ngOnInit(): void {
        this.activityRegistrationInfo.selectedDay = dataCore.getMomentStartOfDay(moment(this.selectedDate));

        this.selectedDayChanged.next(this.activityRegistrationInfo.selectedDay);

        this.workplaceService
            .getWorkplaces()
            .subscribe((res: HttpResponse<Nema.Crew.Model.Workplace[]>) => {
                this.workplaces = res.body;
                this.activityRegistrationInfo.workplace = this.workplaces[0];
                this.registeredActivitiesFilter.workplace = this.workplaces.find(x => x.Id === this.selectedWorkplaceId);
            },
                error => this.onError(error));

        this.workgroupService
            .getWorkgroups()
            .subscribe((res: HttpResponse<Nema.Crew.Model.WorkGroup[]>) => {
                this.workgroups = res.body;
                this.registeredActivitiesFilter.workgroup = this.workgroups.find(x => x.Id === this.selectedWorkgroupId);
            },
                error => this.onError(error));

        this.activityTypeService
            .getActivityTypes()
            .subscribe((res: HttpResponse<Nema.Crew.Model.ActivityType[]>) => {
                this.activityTypes = res.body;
                this.activityRegistrationInfo.activity = this.activityTypes[0];
            },
                error => this.onError(error));

        this.jobTypeService
            .getJobTypes()
            .subscribe((res: HttpResponse<Nema.Crew.Model.JobType[]>) => {
                this.jobTypes = res.body;
            },
                error => this.onError(error));
    }

    ngOnDestroy() {
        // Clean up subscription
        if (this.selectedDayChangedSubscription) {
            this.selectedDayChangedSubscription.unsubscribe();
            this.selectedDayChangedSubscription = null;
        }
    }

    loadDataForDay(day: Moment) {
        const startOfDay = dataCore.getStartOfDay(day);

        this.isFetchingActivities = true;
        this.activityService
            .getActivitiesByWorkplace(startOfDay)
            .subscribe((res: HttpResponse<WorkplaceActivityDto[]>) => {
                this.workplaceActivities = res.body;
                this.isFetchingActivities = false;
            },
                error => {
                    this.onError(error);
                    this.isFetchingActivities = false;
                });

        this.isFetchingPersons = true;
        this.personService
            .getPersonsHavingNoActivityOnDay(startOfDay)
            .subscribe((res: HttpResponse<Nema.Crew.Model.Person[]>) => {
                this.persons = res.body;
                this.isFetchingPersons = false;
            },
                error => {
                    this.onError(error);
                    this.isFetchingPersons = false;
                });

        console.log("Loading data for day, " + this.activityRegistrationInfo.selectedDay.format("DD-MM-YYYY"));
    }

    onDateChanged($event: Moment) {
        this.activityRegistrationInfo.selectedDay = dataCore.getMomentStartOfDay($event);
    }

    registerActivities() {

        this.isLoading = true;

        this.activityService
            .registerActivities(this.activityRegistrationInfo)
            .subscribe(_ => {
                this.loadDataForDay(this.activityRegistrationInfo.selectedDay);
                this.isLoading = false;
                this.snackBarService.showMessage(`Activities have been registered`);
            },
                error => {
                    this.isLoading = false;
                    this.onError(error);
                });
    }

    updateActivity(activity: Nema.Crew.Model.Activity) {
        this.isLoading = true;

        this.activityService
            .updateActivity(activity)
            .subscribe(_ => {
                this.loadDataForDay(this.activityRegistrationInfo.selectedDay);
                this.isLoading = false;
                this.snackBarService.showMessage(`Updated activity for ${activity.Person.Name}`);
            },
                error => {
                    this.isLoading = false;
                    this.onError(error);
                });
    }

    deleteActivity(activity: Nema.Crew.Model.Activity) {
        this.isLoading = true;
        this.activityService
            .deleteActivity(activity.Id)
            .subscribe(_ => {
                this.loadDataForDay(this.activityRegistrationInfo.selectedDay);
                this.isLoading = false;
                this.snackBarService.showMessage(`Deleted activity for ${activity.Person.Name} `);
            },
                error => {
                    this.isLoading = false;
                    this.onError(error);
                });
    }

    deleteAllActivities() {
        var dialogRef = this.dialog
            .open(ConfirmDialog);

        dialogRef.componentInstance.content =
            `Do you want to delete all activities for ${this.activityRegistrationInfo.selectedDay.format("DD-MM-YYYY")}?`;

        dialogRef
            .afterClosed()
            .subscribe(res => this.onDeleteAllDayActivities(res));
    }

    private onDeleteAllDayActivities(shouldDelete?: boolean) {
        if (shouldDelete) {
            this.isLoading = true;
            this.activityService
                .deleteAllDayActivities(dataCore.getStartOfDay(this.activityRegistrationInfo.selectedDay))
                .subscribe(_ => {
                    this.loadDataForDay(this.activityRegistrationInfo.selectedDay);
                    this.isLoading = false;
                    this.snackBarService.showMessage(
                        `Deleted all activities for day, ${this.activityRegistrationInfo.selectedDay.format("DD-MM-YYYY")}`);
                },
                    error => {
                        this.isLoading = false;
                        this.onError(error);
                    });
        }
    }

    private onError(error) {

        let errorMsg = JSON.stringify(error);
        try {
            errorMsg = error.error.ExceptionMessage;
        }
        catch { }
        
        this.snackBarService.showMessage(`${errorMsg}`, 10000)
        console.error(errorMsg);
    }
}

export interface ActivityRegistrationInfoDto {
    selectedDay: Moment;
    persons: Nema.Crew.Model.Person[];
    workplace: Nema.Crew.Model.Workplace;
    workgroup: Nema.Crew.Model.WorkGroup;
    activity: Nema.Crew.Model.ActivityType;
}

interface RegisteredActivitiesFilter {
    workplace: Nema.Crew.Model.Workplace;
    workgroup: Nema.Crew.Model.WorkGroup;
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/activities/daily-activities/day-activities.component.ts